import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, ChevronRight, Download, X } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { trustedByLogos } from '../data/mock';
import { HeroCanvas } from '../components/HeroCanvas';
import { DiagnosticsInstruments } from '../components/DiagnosticsInstruments';
import { ImpactFilmModal } from '../components/ImpactFilm';

// Organization structured data (homepage) - RESILIGENT + owned product Impact AS Plus
const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "RESILIGENT",
  "url": "https://resiligent.com",
  "logo": "https://resiligent.com/opt/brand/resiligent-logo-rebrand-06.webp",
  "address": [
    {
      "@type": "PostalAddress",
      "addressLocality": "Lagos",
      "addressRegion": "Lagos State",
      "addressCountry": "NG"
    },
    {
      "@type": "PostalAddress",
      "addressLocality": "London",
      "addressCountry": "GB"
    }
  ],
  "sameAs": [
    "https://www.linkedin.com/company/resiligent",
    "https://web.facebook.com/resiligent",
    "https://www.instagram.com/resiligent/",
    "https://twitter.com/resiligent1"
  ],
  "owns": {
    "@type": "Product",
    "name": "Impact AS Plus",
    "url": "https://impactasportal.com"
  }
};

// CountUp component for animated numbers
const CountUp = ({ end, duration = 2000, suffix = '', prefix = '' }) => {
  // Start at the real final value so crawlers (and no-interaction renders) read the true
  // figure, never 0. The count-up animation runs ONCE for visitors, and only after a real
  // user interaction (scroll / pointer / key), which search-engine renderers never perform,
  // even with their tall viewport. So crawlers always read the real value.
  const [count, setCount] = useState(end);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return; // keep the static real value, no animation

    let animationFrame;

    const startAnimation = () => {
      if (started.current) return;
      const rect = ref.current?.getBoundingClientRect();
      if (!rect || rect.top > window.innerHeight || rect.bottom < 0) return; // not in view yet
      started.current = true;
      let startTime;
      const animate = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / duration, 1);
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        setCount(Math.floor(easeOutQuart * end));
        if (progress < 1) {
          animationFrame = requestAnimationFrame(animate);
        } else {
          setCount(end); // settle exactly on the real value, no loop
        }
      };
      animationFrame = requestAnimationFrame(animate);
    };

    window.addEventListener('scroll', startAnimation, { passive: true });
    window.addEventListener('pointermove', startAnimation, { passive: true });
    window.addEventListener('keydown', startAnimation);

    return () => {
      window.removeEventListener('scroll', startAnimation);
      window.removeEventListener('pointermove', startAnimation);
      window.removeEventListener('keydown', startAnimation);
      if (animationFrame) cancelAnimationFrame(animationFrame);
    };
  }, [end, duration]);

  return (
    <span ref={ref}>
      {prefix}{count}{suffix}
    </span>
  );
};

// Service link cards for below hero (using original Core Domains)
const serviceLinks = [
  { title: "Diagnostics & Insights", href: "/diagnostics" },
  { title: "Coaching & Development", href: "/coaching" },
  { title: "Capability Building", href: "/capability-building" },
  { title: "Advisory & Change", href: "/advisory" },
  { title: "Career Selection & Assessment Centres", href: "/early-career-selection" },
];

// Core Domains data
const coreDomains = [
  {
    title: "Diagnostics & Insights",
    description: "Proprietary and partner assessments that turn leadership decisions into evidence-based actions.",
    href: "/diagnostics"
  },
  {
    title: "Coaching & Leadership Development",
    description: "Confidential, accredited coaching that accelerates judgement, resilience, and performance.",
    href: "/coaching"
  },
  {
    title: "Capability Building and Training",
    description: "Bespoke programmes that build capability across career stages and leadership levels.",
    href: "/capability-building"
  },
  {
    title: "Advisory & Organisational Change",
    description: "Culture and behavioural alignment that supports execution, trust, and transformation.",
    href: "/advisory"
  },
  {
    title: "Career Selection & Assessment Centres",
    description: "Structured assessment and selection processes for graduate recruitment and leadership appointments.",
    href: "/early-career-selection"
  },
];

const HomePage = () => {
  // State for corporate profile download modal
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showImpactFilm, setShowImpactFilm] = useState(false);
  const [downloadForm, setDownloadForm] = useState({ name: '', company: '', email: '' });
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  const CORPORATE_PROFILE_URL = "https://customer-assets.emergentagent.com/job_9dea0385-ce0b-4daa-adfc-ca84c0eb189a/artifacts/e56czl5s_RESILIGENT%20Corporate%20Profile%20%272026.pdf";

  const handleDownloadSubmit = async (e) => {
    e.preventDefault();
    setDownloadLoading(true);
    setDownloadError('');

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/profile-downloads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(downloadForm)
      });

      if (response.ok) {
        // Trigger download
        window.open(CORPORATE_PROFILE_URL, '_blank');
        setShowDownloadModal(false);
        setDownloadForm({ name: '', company: '', email: '' });
      } else {
        setDownloadError('Something went wrong. Please try again.');
      }
    } catch (err) {
      setDownloadError('Something went wrong. Please try again.');
    } finally {
      setDownloadLoading(false);
    }
  };

  // Handle video play with accessibility
  const handleVideoPlay = () => {
    const video = document.getElementById('intro-video');
    const overlay = document.getElementById('video-overlay');
    if (video && overlay) {
      video.play();
      overlay.style.opacity = '0';
      overlay.style.pointerEvents = 'none';
      video.focus();
    }
  };

  // Proof band logos (homepage only) — ordered rows; excludes Secondary and Webclip
  const logoByName = Object.fromEntries(trustedByLogos.map((l) => [l.name, l]));
  const africaRowLogos = [
    "Central Bank of Nigeria", "African Development Bank", "Bank of Industry", "Union Bank",
    "Polaris Bank", "NLNG", "Lagos Business School", "Delta State", "Life Flour Mill",
    "Fenchurch Group", "Fenchurch Energy", "Fenchurch Power", "Kedari Capital", "MBO Capital", "Michelson"
  ].map((n) => logoByName[n]).filter(Boolean);
  const globalRowLogos = [
    "Deutsche Bank", "Barclays", "BNP Paribas", "Citi", "Scotiabank", "Amazon", "Vodafone",
    "Fujitsu", "Coca-Cola", "Danone", "JTI", "Irish Distillers", "Booking.com", "Molten Ventures", "Utilita"
  ].map((n) => logoByName[n]).filter(Boolean);

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Leadership Development Nigeria | Executive Coaching Lagos | RESILIGENT Africa"
        description="RESILIGENT is Nigeria's leading leadership development and executive coaching firm. ICF MCC certified coaches delivering leadership intelligence, diagnostics, and organisational transformation across Africa. Trusted by top Nigerian banks, multinationals, and government agencies."
        keywords="leadership development Nigeria, executive coaching Lagos, leadership consulting Africa, organisational development Nigeria, ICF certified coach Nigeria, leadership training Lagos, executive coaching West Africa, leadership intelligence Africa, board advisory Nigeria, leadership diagnostics Lagos"
        canonical="/"
        jsonLd={organizationSchema}
      />
      
      {/* Skip to main content link for accessibility */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[10000] focus:bg-white focus:text-[#2693d0] focus:px-4 focus:py-2 focus:rounded focus:font-medium"
      >
        Skip to main content
      </a>
      
      <Header />
      
      <main id="main-content" role="main">
        {/* HERO SECTION */}
        <section 
          className="relative min-h-[100svh] sm:min-h-[85vh] flex items-center bg-[#0a0a0a] overflow-hidden"
          aria-labelledby="hero-heading"
        >
          {/* Drawn brand-colour background (canvas), behind content */}
          <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
            <HeroCanvas />
          </div>

          {/* Readability overlay between background and content */}
          {/* Hero background drift animation */}
          <style>{`
            .hero-canvas {
              width: 150%;
              animation: hero-drift 30s linear infinite alternate;
            }
            @keyframes hero-drift {
              from { transform: translateX(0); }
              to { transform: translateX(-33%); }
            }
            @media (prefers-reduced-motion: reduce) {
              .hero-canvas {
                animation: none;
              }
            }
          `}</style>

          <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-32 pb-16 sm:pb-20">
            <div className="max-w-4xl mx-auto text-center">
              <h1 
                id="hero-heading"
                className="text-white font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-[1.1] sm:leading-[1.05] tracking-tight mb-6 sm:mb-8"
              >
                Leadership Intelligence for Organisations Navigating Complexity
              </h1>
              
              <p className="text-slate-300 text-lg sm:text-xl lg:text-2xl leading-relaxed mb-3 sm:mb-4 max-w-3xl mx-auto">
                We develop leaders, teams, and cultures that perform under pressure, across Africa and beyond.
              </p>
              
              <p className="text-slate-500 text-sm sm:text-base mb-8 sm:mb-10">
                Trusted by global banks, regulators, multinationals, and public institutions.
              </p>
              
              <div className="flex items-center justify-center">
                <a
                  href="/engagement"
                  className="inline-flex items-center gap-2 sm:gap-3 bg-[#2693d0] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg hover:bg-[#1e7ab8] focus:outline-none focus:ring-2 focus:ring-[#2693d0] focus:ring-offset-2 focus:ring-offset-[#0a0a0a] transition-all duration-300"
                  aria-label="Find out how RESILIGENT can help your organisation"
                >
                  Find Out How
                  <ArrowRight size={20} aria-hidden="true" />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* SCROLLING LOGO STRIP */}
        <section className="bg-[#f8fafb] py-6 sm:py-8 overflow-hidden" aria-label="Some of the organisations we have worked with">
          <p className="text-slate-400 text-xs sm:text-sm text-center mb-4 sm:mb-5">
            Some of the organisations we have worked with
          </p>
          <div 
            className="logo-scroll-container group"
            onMouseEnter={(e) => {
              const inner = e.currentTarget.querySelector('.logo-scroll-inner');
              if (inner) inner.style.animationPlayState = 'paused';
            }}
            onMouseLeave={(e) => {
              const inner = e.currentTarget.querySelector('.logo-scroll-inner');
              if (inner) inner.style.animationPlayState = 'running';
            }}
          >
            <div className="logo-scroll-inner flex items-center gap-10 sm:gap-14">
              {[...africaRowLogos, ...globalRowLogos, ...africaRowLogos, ...globalRowLogos].map((logo, index) => (
                <img
                  key={`scroll-logo-${index}`}
                  src={logo.url}
                  alt={logo.name}
                  className="h-8 sm:h-10 w-auto object-contain flex-shrink-0 opacity-70 grayscale-0"
                  loading="lazy"
                />
              ))}
            </div>
          </div>
          <style>{`
            .logo-scroll-container {
              width: 100%;
              overflow: hidden;
            }
            .logo-scroll-inner {
              display: flex;
              width: max-content;
              animation: scroll-logos 60s linear infinite;
            }
            @keyframes scroll-logos {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            @media (prefers-reduced-motion: reduce) {
              .logo-scroll-inner {
                animation: none;
              }
            }
          `}</style>
        </section>

        {/* STATS SECTION */}
        <section className="bg-[#e8f2f7] py-10 sm:py-12 lg:py-16" aria-label="Company statistics">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-10 lg:gap-14">
              <div className="text-center">
                <p className="text-[#2693d0] text-4xl sm:text-5xl lg:text-6xl font-bold mb-1 sm:mb-2" aria-label="15 plus years">
                  <CountUp end={15} duration={2000} suffix="+" />
                </p>
                <p className="text-slate-600 text-xs sm:text-sm">Years building leadership capability across Africa</p>
              </div>
              <div className="text-center">
                <p className="text-[#2693d0] text-4xl sm:text-5xl lg:text-6xl font-bold mb-1 sm:mb-2">
                  <CountUp end={4} duration={1500} />
                </p>
                <p className="text-slate-600 text-xs sm:text-sm">ICF Master Certified Coaches on faculty</p>
              </div>
              <div className="text-center">
                <p className="text-[#2693d0] text-4xl sm:text-5xl lg:text-6xl font-bold mb-1 sm:mb-2">
                  <CountUp end={67} duration={2000} suffix="%" />
                </p>
                <p className="text-slate-600 text-xs sm:text-sm">Measurable behaviour change in leadership cohorts</p>
              </div>
              <div className="text-center">
                <p className="text-[#2693d0] text-4xl sm:text-5xl lg:text-6xl font-bold mb-1 sm:mb-2">
                  <CountUp end={100} duration={2500} suffix="+" />
                </p>
                <p className="text-slate-600 text-xs sm:text-sm">Organisations transformed</p>
              </div>
            </div>
          </div>
        </section>

        {/* OUR APPROACH SECTION - 4 Phase Design */}
        <section className="relative bg-slate-100 py-10 sm:py-14 lg:py-16 overflow-hidden" aria-labelledby="approach-heading">
          
          {/* Subtle Background Circles */}
          <div className="absolute inset-0" aria-hidden="true">
            <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-[#00A6DA]/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-1/4 left-1/4 w-48 h-48 bg-[#0D7CB4]/10 rounded-full blur-3xl"></div>
            <div className="absolute top-1/2 left-1/2 w-32 h-32 bg-[#00A6DA]/5 rounded-full blur-2xl"></div>
          </div>
          
          {/* Content */}
          <div className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 xl:px-20">
            <div className="max-w-4xl">
              {/* Headline */}
              <h2 id="approach-heading" className="text-slate-900 font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl mb-2 sm:mb-3 leading-[1.1]">
                Leadership is a system.
              </h2>
              <p className="text-slate-500 text-sm sm:text-base mb-4 sm:mb-6">Our proven methodology</p>
              
              {/* Divider */}
              <div className="w-full h-px bg-slate-300 mb-4 sm:mb-6" aria-hidden="true"></div>
              
              {/* Description */}
              <p className="text-slate-600 text-sm sm:text-base leading-relaxed mb-6 sm:mb-10">
                Our work follows a disciplined, insight-led approach designed for regulated and high-stakes environments.
              </p>
              
              {/* Four Phase Cards - Responsive grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-10" role="list">
                {/* Step 1 - Diagnose */}
                <article className="group bg-white rounded-xl p-4 sm:p-5 card-pulse-border-1 transition-all duration-300 hover:scale-105 hover:shadow-xl focus-within:ring-2 focus-within:ring-[#2693d0]" role="listitem">
                  <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                    <span className="text-[#2693d0] text-3xl sm:text-4xl font-bold" aria-hidden="true">01</span>
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-[#2693d0]" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <circle cx="11" cy="11" r="8" strokeWidth="2" />
                      <path strokeLinecap="round" strokeWidth="2" d="M21 21l-4.35-4.35" />
                    </svg>
                  </div>
                  <h3 className="text-slate-900 font-bold text-sm sm:text-base mb-1 sm:mb-2">Diagnose</h3>
                  <p className="text-slate-500 text-xs leading-relaxed">
                    We use our proprietary Impact AS Plus™ system to surface leadership, cultural, and execution signals.
                  </p>
                </article>
                
                {/* Step 2 - Prioritise */}
                <article className="group bg-white rounded-xl p-4 sm:p-5 card-pulse-border-2 transition-all duration-300 hover:scale-105 hover:shadow-xl focus-within:ring-2 focus-within:ring-[#2693d0]" role="listitem">
                  <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                    <span className="text-[#2693d0] text-3xl sm:text-4xl font-bold" aria-hidden="true">02</span>
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-[#2693d0]" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <circle cx="12" cy="12" r="3" strokeWidth="2" />
                      <circle cx="12" cy="12" r="8" strokeWidth="2" />
                    </svg>
                  </div>
                  <h3 className="text-slate-900 font-bold text-sm sm:text-base mb-1 sm:mb-2">Prioritise</h3>
                  <p className="text-slate-500 text-xs leading-relaxed">
                    We help leadership teams interpret insight, identify patterns, and focus on what shifts performance.
                  </p>
                </article>
                
                {/* Step 3 - Activate */}
                <article className="group bg-white rounded-xl p-4 sm:p-5 card-pulse-border-3 transition-all duration-300 hover:scale-105 hover:shadow-xl focus-within:ring-2 focus-within:ring-[#2693d0]" role="listitem">
                  <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                    <span className="text-[#2693d0] text-3xl sm:text-4xl font-bold" aria-hidden="true">03</span>
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-[#2693d0]" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <h3 className="text-slate-900 font-bold text-sm sm:text-base mb-1 sm:mb-2">Activate</h3>
                  <p className="text-slate-500 text-xs leading-relaxed">
                    Through coaching, development, and interventions, we translate insight into sustained behaviour change.
                  </p>
                </article>
                
                {/* Step 4 - Sustain */}
                <article className="group bg-white rounded-xl p-4 sm:p-5 card-pulse-border-4 transition-all duration-300 hover:scale-105 hover:shadow-xl focus-within:ring-2 focus-within:ring-[#2693d0]" role="listitem">
                  <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                    <span className="text-[#2693d0] text-3xl sm:text-4xl font-bold" aria-hidden="true">04</span>
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-[#2693d0]" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </div>
                  <h3 className="text-slate-900 font-bold text-sm sm:text-base mb-1 sm:mb-2">Sustain</h3>
                  <p className="text-slate-500 text-xs leading-relaxed">
                    We help organisations monitor progress, reinforce accountability, and embed discipline over time.
                  </p>
                </article>
              </div>
              
              {/* Bottom Text */}
              <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                From graduate recruitment and assessment centres, through to leadership development, coaching, and governance, <span className="text-[#00A6DA] font-medium">RESILIGENT supports the full talent and leadership lifecycle through Impact AS Plus™</span>.
              </p>
            </div>
          </div>
        </section>

        {/* INTRODUCING RESILIGENT VIDEO SECTION */}
        <section className="bg-[#0a0a0a] py-12 sm:py-16 lg:py-24" aria-labelledby="video-heading">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 sm:mb-10">
              <p className="text-white/60 text-xs sm:text-sm font-medium uppercase tracking-wider mb-3 sm:mb-4">
                Introducing RESILIGENT
              </p>
              <h2 id="video-heading" className="text-white font-bold text-xl sm:text-2xl md:text-3xl lg:text-4xl leading-tight max-w-3xl mx-auto mb-3 sm:mb-4">
                A leadership advisory firm built on evidence, experience, and impact
              </h2>
              <p className="text-white/70 text-sm sm:text-base lg:text-lg max-w-2xl mx-auto">
                Discover how we work with organisations and the tangible outcomes we enable leaders to achieve.
              </p>
            </div>
            
            {/* Video Container */}
            <div className="relative max-w-4xl mx-auto">
              <div className="relative aspect-video rounded-lg sm:rounded-xl overflow-hidden bg-[#0a1628] shadow-2xl">
                <video 
                  id="intro-video"
                  className="w-full h-full object-cover"
                  preload="metadata"
                  playsInline
                  controls
                  poster="/opt/photo/screenshot-2026-01-08-at-19-25-48.webp"
                  aria-label="RESILIGENT introduction video"
                >
                  <source 
                    src="https://customer-assets.emergentagent.com/job_f1101ff4-5c3a-4b22-9c23-7958ffd82067/artifacts/xwcxxl6x_Resiligent%20Intro%20Video.mp4" 
                    type="video/mp4" 
                  />
                  Your browser does not support the video tag.
                </video>
                
                {/* Play Button Overlay */}
                <button 
                  id="video-overlay"
                  className="absolute inset-0 bg-black/30 flex flex-col items-center justify-center cursor-pointer transition-opacity duration-300 focus:outline-none focus:ring-2 focus:ring-[#2693d0] focus:ring-inset"
                  onClick={() => {
                    const video = document.getElementById('intro-video');
                    const overlay = document.getElementById('video-overlay');
                    if (video && overlay) {
                      video.play();
                      overlay.style.opacity = '0';
                      overlay.style.pointerEvents = 'none';
                    }
                  }}
                  aria-label="Play introduction video"
                >
                  <div className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-full bg-black/40 backdrop-blur-sm border-2 border-white/50 flex items-center justify-center transition-all duration-300 hover:bg-black/50 hover:scale-110">
                    <svg className="w-6 h-6 sm:w-8 sm:h-8 lg:w-10 lg:h-10 text-white ml-1" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </div>
                  <span className="text-white text-xs sm:text-sm lg:text-base font-medium mt-3 sm:mt-4 drop-shadow-lg">
                    Watch the introduction →
                  </span>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* CORE DOMAINS SECTION - Bento Grid Style */}
        <section className="bg-white py-14 sm:py-20 lg:py-28" aria-labelledby="domains-alt-heading">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 id="domains-alt-heading" className="text-slate-900 font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-tight text-center mb-4">
              Our Core Domains
            </h2>
            <p className="text-slate-500 text-center text-base sm:text-lg mb-4 max-w-2xl mx-auto">
              Integrated solutions across the full leadership lifecycle
            </p>
            <p className="text-slate-400 text-center text-sm sm:text-base mb-10 sm:mb-14 max-w-3xl mx-auto">
              Our domains reflect how leadership challenges evolve over time. Each can stand alone, but delivers the greatest impact when integrated, from early talent decisions to senior leadership transition and succession.
            </p>
            
            {/* Bento Grid Layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              
              {/* Card 1 - Diagnostics & Insights (Large, with image) */}
              <a href="/diagnostics" className="group relative block md:col-span-2 lg:col-span-2 h-[280px] sm:h-[320px] rounded-2xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#00A6DA] focus:ring-offset-2 animate-float cursor-pointer" aria-label="Learn more about Diagnostics and Insights services">
                {/* Layer 1 - Default State */}
                <div className="absolute inset-0 transition-opacity duration-300 group-hover:opacity-0">
                  <img 
                    src="/opt/photo/ext/u1551288049-bebda4e38f71.webp"
                    alt="Leadership assessment and 360 feedback analytics dashboard Nigeria"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
                    <h3 className="text-white font-bold text-xl sm:text-2xl uppercase tracking-wide">Diagnostics & Insights</h3>
                  </div>
                </div>
                {/* Layer 2 - Hover State (White background) */}
                <div className="absolute inset-0 bg-white card-pulse-border rounded-2xl opacity-0 transition-opacity duration-300 group-hover:opacity-100 flex flex-col justify-between p-6 sm:p-8">
                  <div>
                    <h3 className="text-[#0a1628] font-bold text-xl sm:text-2xl mb-3">Diagnostics & Insights</h3>
                    <p className="text-slate-600 text-sm sm:text-base max-w-lg">Evidence-based diagnostics that surface behavioural patterns, leadership risk, and organisational signals, enabling confident decisions across individuals, teams, and enterprises.</p>
                  </div>
                  <div>
                    <span className="inline-block bg-[#0a1628] text-white px-6 py-3 rounded-lg font-semibold text-sm hover:bg-[#1a3a5c] transition-colors">
                      Explore Domain
                    </span>
                  </div>
                </div>
              </a>
              
              {/* Card 2 - Coaching & Development (Blue gradient with wave) */}
              <a href="/coaching" className="group relative block h-[280px] sm:h-[320px] rounded-2xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#00A6DA] focus:ring-offset-2 animate-float-1 cursor-pointer">
                {/* Layer 1 - Default State */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#1a3a5c] to-[#2693d0] transition-opacity duration-300 group-hover:opacity-0">
                  <div className="absolute inset-0">
                    <svg className="absolute bottom-0 w-full h-32 opacity-30" viewBox="0 0 400 100" preserveAspectRatio="none">
                      <path d="M0,50 Q100,0 200,50 T400,50 L400,100 L0,100 Z" fill="white" />
                    </svg>
                    <svg className="absolute bottom-0 w-full h-24 opacity-20" viewBox="0 0 400 100" preserveAspectRatio="none">
                      <path d="M0,60 Q100,20 200,60 T400,60 L400,100 L0,100 Z" fill="white" />
                    </svg>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
                    <h3 className="text-white font-bold text-xl sm:text-2xl uppercase tracking-wide">Coaching & Leadership Development</h3>
                  </div>
                </div>
                {/* Layer 2 - Hover State (White background) */}
                <div className="absolute inset-0 bg-white card-pulse-border rounded-2xl opacity-0 transition-opacity duration-300 group-hover:opacity-100 flex flex-col justify-between p-6 sm:p-8">
                  <div>
                    <h3 className="text-[#0a1628] font-bold text-xl sm:text-2xl mb-3">Coaching & Leadership Development</h3>
                    <p className="text-slate-600 text-sm sm:text-base">ICF-accredited coaching supporting leaders to strengthen judgement, presence, and performance, with a clear focus on sustained behavioural change.</p>
                  </div>
                  <div>
                    <span className="inline-block bg-[#0a1628] text-white px-6 py-3 rounded-lg font-semibold text-sm hover:bg-[#1a3a5c] transition-colors">
                      Explore Domain
                    </span>
                  </div>
                </div>
              </a>
              
              {/* Card 3 - Capability Building (with image) */}
              <a href="/capability-building" className="group relative block h-[280px] sm:h-[320px] rounded-2xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#00A6DA] focus:ring-offset-2 animate-float-2 cursor-pointer" aria-label="Learn more about Capability Building and Training programmes">
                {/* Layer 1 - Default State */}
                <div className="absolute inset-0 transition-opacity duration-300 group-hover:opacity-0">
                  <img 
                    src="/opt/photo/capability-building.webp"
                    alt="Leadership training workshop with African executives in Lagos Nigeria"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
                    <h3 className="text-white font-bold text-xl sm:text-2xl uppercase tracking-wide">Capability Building & Training</h3>
                  </div>
                </div>
                {/* Layer 2 - Hover State (White background) */}
                <div className="absolute inset-0 bg-white card-pulse-border rounded-2xl opacity-0 transition-opacity duration-300 group-hover:opacity-100 flex flex-col justify-between p-6 sm:p-8">
                  <div>
                    <h3 className="text-[#0a1628] font-bold text-xl sm:text-2xl mb-3">Capability Building & Training</h3>
                    <p className="text-slate-600 text-sm sm:text-base">Bespoke leadership and management programmes designed to build practical capability at every career stage, from emerging leaders to senior executives.</p>
                  </div>
                  <div>
                    <span className="inline-block bg-[#0a1628] text-white px-6 py-3 rounded-lg font-semibold text-sm hover:bg-[#1a3a5c] transition-colors">
                      Explore Domain
                    </span>
                  </div>
                </div>
              </a>
              
              {/* Card 4 - Advisory (Teal with abstract pattern) */}
              <a href="/advisory" className="group relative block h-[280px] sm:h-[320px] rounded-2xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#00A6DA] focus:ring-offset-2 animate-float-3 cursor-pointer">
                {/* Layer 1 - Default State */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#0D7CB4] to-[#00A6DA] transition-opacity duration-300 group-hover:opacity-0">
                  <div className="absolute inset-0">
                    <svg className="w-full h-full" viewBox="0 0 300 320" preserveAspectRatio="none">
                      <path d="M-20,80 Q80,40 180,100 Q280,160 320,120" stroke="white" strokeWidth="1.5" fill="none" opacity="0.2" />
                      <path d="M-20,120 Q100,70 200,140 Q300,210 340,160" stroke="white" strokeWidth="1.2" fill="none" opacity="0.15" />
                      <path d="M-20,160 Q60,120 160,180 Q260,240 320,200" stroke="white" strokeWidth="1" fill="none" opacity="0.12" />
                      <path d="M0,260 Q75,230 150,260 T300,240 L300,320 L0,320 Z" fill="white" fillOpacity="0.08" />
                      <path d="M0,280 Q75,250 150,280 T300,260 L300,320 L0,320 Z" fill="white" fillOpacity="0.12" />
                      <path d="M220,30 Q270,60 260,120" stroke="white" strokeWidth="2" fill="none" opacity="0.18" />
                      <path d="M240,20 Q300,55 285,130" stroke="white" strokeWidth="1.5" fill="none" opacity="0.12" />
                      <path d="M30,200 Q-10,150 40,100" stroke="white" strokeWidth="1.5" fill="none" opacity="0.15" />
                    </svg>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
                    <h3 className="text-white font-bold text-xl sm:text-2xl uppercase tracking-wide">Advisory & Organisational Change</h3>
                  </div>
                </div>
                {/* Layer 2 - Hover State (White background) */}
                <div className="absolute inset-0 bg-white card-pulse-border rounded-2xl opacity-0 transition-opacity duration-300 group-hover:opacity-100 flex flex-col justify-between p-6 sm:p-8">
                  <div>
                    <h3 className="text-[#0a1628] font-bold text-xl sm:text-2xl mb-3">Advisory & Organisational Change</h3>
                    <p className="text-slate-600 text-sm sm:text-base">Strategic advisory supporting culture alignment, leadership execution, and organisational trust through periods of complexity and change.</p>
                  </div>
                  <div>
                    <span className="inline-block bg-[#0a1628] text-white px-6 py-3 rounded-lg font-semibold text-sm hover:bg-[#1a3a5c] transition-colors">
                      Explore Domain
                    </span>
                  </div>
                </div>
              </a>
              
              {/* Card 5 - Career Selection & Assessment Centres (single card in row with image) */}
              <a href="/early-career-selection" className="group relative block h-[280px] sm:h-[320px] rounded-2xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#00A6DA] focus:ring-offset-2 animate-float-1 cursor-pointer">
                {/* Layer 1 - Default State */}
                <div className="absolute inset-0 transition-opacity duration-300 group-hover:opacity-0">
                  <img 
                    src="/opt/photo/pexels-fauxels-3183197-1701671875.webp"
                    alt="Team collaboration around a table with laptops for career selection assessment"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
                    <h3 className="text-white font-bold text-xl sm:text-2xl uppercase tracking-wide">Career Selection & Assessment Centres</h3>
                  </div>
                </div>
                {/* Layer 2 - Hover State (White background) */}
                <div className="absolute inset-0 bg-white card-pulse-border rounded-2xl opacity-0 transition-opacity duration-300 group-hover:opacity-100 flex flex-col justify-between p-6 sm:p-8">
                  <div>
                    <h3 className="text-[#0a1628] font-bold text-xl sm:text-2xl mb-3">Career Selection & Assessment Centres</h3>
                    <p className="text-slate-600 text-sm sm:text-base">Structured assessment and selection for graduate and early-career talent, powered by JobFit™ and InsightEdge™ diagnostics.</p>
                  </div>
                  <div>
                    <span className="inline-block bg-[#0a1628] text-white px-6 py-3 rounded-lg font-semibold text-sm hover:bg-[#1a3a5c] transition-colors">
                      Explore Domain
                    </span>
                  </div>
                </div>
              </a>
              
              {/* Card 6 - Leadership & Succession Intelligence (Full width, Navy blue with geometric pattern) */}
              <a href="/leadership-succession-intelligence" className="group relative block md:col-span-2 lg:col-span-3 h-[240px] sm:h-[280px] rounded-2xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#00A6DA] focus:ring-offset-2 animate-float-2 cursor-pointer">
                {/* Layer 1 - Default State */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#074a6d] to-[#0a6694] transition-opacity duration-300 group-hover:opacity-0">
                  <div className="absolute inset-0">
                    <svg className="w-full h-full" viewBox="0 0 800 280" preserveAspectRatio="none">
                      <polygon points="100,40 130,55 130,85 100,100 70,85 70,55" stroke="white" strokeWidth="1" fill="none" opacity="0.15" />
                      <polygon points="160,70 190,85 190,115 160,130 130,115 130,85" stroke="white" strokeWidth="1" fill="none" opacity="0.12" />
                      <polygon points="100,100 130,115 130,145 100,160 70,145 70,115" stroke="white" strokeWidth="1" fill="none" opacity="0.1" />
                      <polygon points="650,30 680,45 680,75 650,90 620,75 620,45" stroke="white" strokeWidth="1" fill="none" opacity="0.18" />
                      <polygon points="710,60 740,75 740,105 710,120 680,105 680,75" stroke="white" strokeWidth="1" fill="none" opacity="0.15" />
                      <polygon points="650,90 680,105 680,135 650,150 620,135 620,105" stroke="white" strokeWidth="1" fill="none" opacity="0.12" />
                      <polygon points="710,120 740,135 740,165 710,180 680,165 680,135" stroke="white" strokeWidth="1" fill="none" opacity="0.1" />
                      <line x1="250" y1="0" x2="350" y2="280" stroke="white" strokeWidth="0.5" opacity="0.08" />
                      <line x1="300" y1="0" x2="400" y2="280" stroke="white" strokeWidth="0.5" opacity="0.06" />
                      <line x1="450" y1="0" x2="550" y2="280" stroke="white" strokeWidth="0.5" opacity="0.08" />
                      <line x1="500" y1="0" x2="600" y2="280" stroke="white" strokeWidth="0.5" opacity="0.06" />
                      <path d="M0,0 L60,0 L60,5 L5,5 L5,60 L0,60 Z" fill="white" fillOpacity="0.1" />
                      <path d="M800,0 L740,0 L740,5 L795,5 L795,60 L800,60 Z" fill="white" fillOpacity="0.1" />
                      <path d="M0,280 L60,280 L60,275 L5,275 L5,220 L0,220 Z" fill="white" fillOpacity="0.08" />
                      <path d="M800,280 L740,280 L740,275 L795,275 L795,220 L800,220 Z" fill="white" fillOpacity="0.08" />
                      <rect x="380" y="50" width="8" height="8" fill="white" fillOpacity="0.15" />
                      <rect x="400" y="70" width="6" height="6" fill="white" fillOpacity="0.12" />
                      <rect x="360" y="80" width="5" height="5" fill="white" fillOpacity="0.1" />
                      <rect x="420" y="100" width="8" height="8" fill="white" fillOpacity="0.15" />
                      <rect x="390" y="120" width="6" height="6" fill="white" fillOpacity="0.12" />
                    </svg>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
                    <h3 className="text-white font-bold text-xl sm:text-2xl uppercase tracking-wide">Leadership & Succession Intelligence</h3>
                  </div>
                </div>
                {/* Layer 2 - Hover State (White background) */}
                <div className="absolute inset-0 bg-white card-pulse-border rounded-2xl opacity-0 transition-opacity duration-300 group-hover:opacity-100 flex flex-col justify-between p-6 sm:p-8">
                  <div>
                    <h3 className="text-[#0a1628] font-bold text-xl sm:text-2xl mb-3">Leadership & Succession Intelligence</h3>
                    <p className="text-slate-600 text-sm sm:text-base max-w-2xl">Data-led insight into leadership readiness, succession pipelines, and transition risk, supporting stronger long-term leadership decisions.</p>
                  </div>
                  <div>
                    <span className="inline-block bg-[#0a1628] text-white px-6 py-3 rounded-lg font-semibold text-sm hover:bg-[#1a3a5c] transition-colors">
                      Explore Domain
                    </span>
                  </div>
                </div>
              </a>
              
            </div>
          </div>
        </section>

        {/* IMPACT AS PLUS SECTION */}
        <section className="relative bg-[#f0f4f8] py-14 sm:py-20 lg:py-28 border-t-4 border-[#2693d0]/10 overflow-hidden" aria-labelledby="impact-heading">
          {/* Visual transition element */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#2693d0]/30 to-transparent" aria-hidden="true" />
          
          {/* Animated Background - Same as Leadership System */}
          <div className="absolute inset-0" aria-hidden="true">
            <img 
              src="/opt/photo/ext/u1630155923002-17db0f219f1f.webp"
              alt="Abstract technology background representing leadership intelligence platform"
              className="absolute inset-0 w-full h-full object-cover opacity-[0.08] animate-ken-burns"
            />
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-3 gap-10 lg:gap-12 items-center">
              
              {/* LEFT - Text Content */}
              <div>
                <img 
                  src="/opt/brand/impactas-final.webp"
                  alt="Impact AS Plus"
                  loading="lazy"
                  className="h-12 lg:h-14 w-auto mb-6 animate-logo-pulse-main"
                />
                
                <h2 id="impact-heading" className="text-slate-900 font-bold text-2xl sm:text-3xl leading-tight mb-6">
                  The Intelligence Behind Our Work
                </h2>
                
                <p className="text-slate-600 text-base leading-relaxed mb-6">
                  Impact AS Plus&trade; connects assessments, development and progress in one record across the leader, the team and the organisation, so the decisions you make about people rest on evidence you can explain and defend.
                </p>
                
                <p className="text-slate-900 text-base font-semibold mb-6">
                  One system. Multiple applications. Measurable impact.
                </p>
                
                <a 
                  href="/impact-as-plus"
                  className="inline-flex items-center gap-2 text-[#2693d0] font-semibold text-base hover:gap-3 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#2693d0] focus:ring-offset-2"
                  aria-label="Learn more about Impact AS Plus"
                >
                  Explore Impact AS Plus™
                  <ArrowRight size={18} aria-hidden="true" />
                </a>

                <button
                  type="button"
                  onClick={() => setShowImpactFilm(true)}
                  className="block mt-4 text-sm text-slate-600 underline underline-offset-4 hover:text-[#2693d0] transition-colors duration-300"
                  data-testid="home-impact-film-link"
                >
                  Watch the Impact AS Plus film
                </button>
                {showImpactFilm && <ImpactFilmModal onClose={() => setShowImpactFilm(false)} />}
              </div>
              
              {/* MIDDLE - Bullet Points (vertically centered) */}
              <div>
                <div className="space-y-6">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#2693d0] mt-2 flex-shrink-0"></div>
                    <div>
                      <h3 className="text-slate-900 font-semibold text-base mb-1">Organisational Intelligence™</h3>
                      <p className="text-slate-600 text-sm">Culture, execution risk, and systemic patterns</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#2693d0] mt-2 flex-shrink-0"></div>
                    <div>
                      <h3 className="text-slate-900 font-semibold text-base mb-1">Team Intelligence™</h3>
                      <p className="text-slate-600 text-sm">Leadership dynamics within groups and committees</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#2693d0] mt-2 flex-shrink-0"></div>
                    <div>
                      <h3 className="text-slate-900 font-semibold text-base mb-1">Individual Intelligence™</h3>
                      <p className="text-slate-600 text-sm">Capability, behaviour, and development priorities</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* RIGHT - Concentric Circles Diagram with 3D Effect and Curved Text */}
              <div className="flex justify-center lg:justify-end" aria-hidden="true">
                <div className="relative w-[300px] h-[300px] sm:w-[340px] sm:h-[340px] animate-breathe" style={{ perspective: '1000px' }}>
                  {/* Organisation - Outer Ring (Darkest) with 3D effect */}
                  <div 
                    className="absolute inset-0 rounded-full"
                    style={{
                      background: 'linear-gradient(145deg, #142d47 0%, #0a1a2e 100%)',
                      boxShadow: 'inset 4px 4px 8px rgba(0,0,0,0.4), inset -2px -2px 6px rgba(255,255,255,0.08), 8px 8px 20px rgba(0,0,0,0.4), -4px -4px 12px rgba(38,147,208,0.1)',
                      border: '1px solid rgba(38,147,208,0.25)'
                    }}
                  />
                  
                  {/* Organisation Label - Centered at top */}
                  <div className="absolute top-3 sm:top-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 z-10">
                    <svg className="w-4 h-4 text-[#00A6DA]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <rect x="3" y="3" width="7" height="7" strokeWidth="2" />
                      <rect x="14" y="3" width="7" height="7" strokeWidth="2" />
                      <rect x="3" y="14" width="7" height="7" strokeWidth="2" />
                      <rect x="14" y="14" width="7" height="7" strokeWidth="2" />
                    </svg>
                    <span className="text-white font-semibold text-xs drop-shadow-lg">Organisation</span>
                  </div>
                  
                  {/* Team - Middle Ring (Medium) with 3D effect */}
                  <div 
                    className="absolute inset-[50px] sm:inset-[60px] rounded-full"
                    style={{
                      background: 'linear-gradient(145deg, #1f5580 0%, #153d5c 100%)',
                      boxShadow: 'inset 3px 3px 6px rgba(0,0,0,0.4), inset -2px -2px 5px rgba(255,255,255,0.08), 6px 6px 15px rgba(0,0,0,0.3), -3px -3px 10px rgba(38,147,208,0.15)',
                      border: '1px solid rgba(38,147,208,0.3)'
                    }}
                  />
                  
                  {/* Team Label - Centered at top of middle ring */}
                  <div className="absolute top-[58px] sm:top-[68px] left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 z-10">
                    <svg className="w-7 h-4 text-[#00A6DA]" fill="none" viewBox="0 0 36 20" stroke="currentColor">
                      <circle cx="6" cy="5" r="3" strokeWidth="2" />
                      <path strokeWidth="2" d="M6 9a4 4 0 00-4 4h8a4 4 0 00-4-4z" />
                      <circle cx="18" cy="5" r="3" strokeWidth="2" />
                      <path strokeWidth="2" d="M18 9a4 4 0 00-4 4h8a4 4 0 00-4-4z" />
                      <circle cx="30" cy="5" r="3" strokeWidth="2" />
                      <path strokeWidth="2" d="M30 9a4 4 0 00-4 4h8a4 4 0 00-4-4z" />
                    </svg>
                    <span className="text-white font-semibold text-xs drop-shadow-lg">Team</span>
                  </div>
                  
                  {/* Individual - Center Circle (Lightest) with 3D effect */}
                  <div 
                    className="absolute inset-[100px] sm:inset-[120px] rounded-full flex items-center justify-center"
                    style={{
                      background: 'linear-gradient(145deg, #2da8e6 0%, #1e7eb8 100%)',
                      boxShadow: 'inset 2px 2px 5px rgba(255,255,255,0.3), inset -2px -2px 5px rgba(0,0,0,0.2), 4px 4px 12px rgba(0,0,0,0.3), -2px -2px 8px rgba(38,147,208,0.3), 0 0 20px rgba(0,166,218,0.3)',
                      border: '2px solid rgba(0,166,218,0.7)'
                    }}
                  >
                    <div className="flex flex-col items-center gap-1">
                      <svg className="w-5 h-5 text-white drop-shadow-md" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span className="text-white font-semibold text-xs sm:text-sm drop-shadow-lg">Individual</span>
                    </div>
                  </div>
                </div>
              </div>
              
            </div>
          </div>
        </section>

        {/* PROPRIETARY LEADERSHIP DIAGNOSTICS SECTION - Restrained, Editorial */}
        <section className="bg-white py-14 sm:py-16 lg:py-20" aria-labelledby="diagnostics-heading">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Section Heading */}
            <h2 
              id="diagnostics-heading" 
              className="text-slate-900 font-semibold text-xl sm:text-2xl mb-6 tracking-tight"
            >
              Proprietary Leadership Diagnostics
            </h2>
            
            {/* Body Paragraph */}
            <p className="text-slate-600 text-base leading-relaxed mb-10 max-w-3xl">
              RESILIGENT has developed and refined a suite of leadership diagnostics that form part of our leadership intelligence capability. Some are proprietary by design, others are adaptive frameworks refined for context. All are integrated through Impact AS Plus™ to support evidence-based leadership decisions across individuals, teams, and organisations.
            </p>
            
            {/* Twelve instruments, grouped */}
            <DiagnosticsInstruments className="mb-10" />
            
            {/* CTA Link */}
            <a 
              href="/diagnostics"
              className="inline-flex items-center text-[#2693d0] text-sm font-medium hover:text-[#1e7ab8] transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#2693d0] focus:ring-offset-2 mt-8"
            >
              Explore our leadership diagnostics
              <ArrowRight size={16} className="ml-1" aria-hidden="true" />
            </a>
          </div>
        </section>

        {/* PROOF SIGNALS SECTION - Stats with Abstract Background */}
        <section className="relative py-20 lg:py-28 overflow-hidden" aria-labelledby="proof-heading">
          {/* Black/grey abstract background image */}
          <div className="absolute inset-0">
            <img 
              src="/opt/photo/ext/u1550684848-fac1c5b4e853.webp"
              alt="Professional business environment representing leadership development outcomes"
              className="absolute inset-0 w-full h-full object-cover grayscale"
            />
            <div className="absolute inset-0 bg-black/60" />
          </div>
          
          <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-8">
            <h2 id="proof-heading" className="text-white font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight text-center mb-16">
              Proof, not promises.
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8 lg:gap-12 max-w-4xl mx-auto">
              <div className="text-center p-10 lg:p-12 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 animate-breathe">
                <p className="text-[#2693d0] text-5xl lg:text-6xl xl:text-7xl font-bold mb-4">67%</p>
                <p className="text-slate-300 text-base lg:text-lg">
                  measurable behaviour change across leadership cohorts
                </p>
              </div>
              <div className="text-center p-10 lg:p-12 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 animate-breathe-delay">
                <p className="text-[#2693d0] text-5xl lg:text-6xl xl:text-7xl font-bold mb-4">8-12</p>
                <p className="text-slate-300 text-base lg:text-lg">
                  months for ROI outcomes to become visible
                </p>
              </div>
            </div>
            
            {/* Testimonial quotes */}
            <div className="grid md:grid-cols-2 gap-6 lg:gap-8 max-w-4xl mx-auto mt-12 lg:mt-16">
              <blockquote className="text-center p-6 lg:p-8 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10">
                <p className="text-slate-300 text-sm sm:text-base leading-relaxed mb-4 italic">
                  "RESILIGENT delivered leadership development from frontline teams through to board level. The experience was practical, refreshing, and sufficiently valuable that I would recommend it as a regular organisational investment."
                </p>
                <footer className="text-slate-400 text-sm font-medium">
                  Omar Shekarau, Executive Director, Bank of Industry
                </footer>
              </blockquote>
              <blockquote className="text-center p-6 lg:p-8 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10">
                <p className="text-slate-300 text-sm sm:text-base leading-relaxed mb-4 italic">
                  "The combination of data analysis and human insight produced strategies that accelerated our growth while addressing our underlying organisational challenges in a sustainable way."
                </p>
                <footer className="text-slate-400 text-sm font-medium">
                  Olufemi Bakare, CEO, Fenchurch Energy Group
                </footer>
              </blockquote>
            </div>
          </div>
        </section>

        {/* BUILT FOR COMPLEXITY SECTION */}
        <section className="relative bg-[#f0f5f8] py-14 sm:py-20 lg:py-28" aria-labelledby="complexity-heading">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#2693d0]/20 to-transparent" aria-hidden="true" />
          
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 id="complexity-heading" className="text-slate-900 font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl mb-6 sm:mb-8 leading-tight">
              Built for complexity. Grounded in Africa.
            </h2>
            <p className="text-slate-600 text-base sm:text-lg lg:text-xl leading-relaxed">
              We combine global standards with deep understanding of African leadership realities: hierarchy, regulation, culture, and pace of change.
            </p>
          </div>
          
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#2693d0]/20 to-transparent" aria-hidden="true" />
        </section>

        {/* FINAL CTA SECTION */}
        <section className="bg-white py-14 sm:py-20 lg:py-28 border-t border-slate-200" aria-labelledby="cta-heading">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 id="cta-heading" className="text-slate-900 font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl mb-4 sm:mb-6 leading-tight">
              Let&apos;s talk about your leadership system.
            </h2>
            <p className="text-slate-600 text-base sm:text-lg lg:text-xl leading-relaxed mb-8 sm:mb-10 max-w-2xl mx-auto">
              A confidential conversation to explore priorities, context, and the outcomes you need.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="/contact"
                className="inline-flex items-center gap-2 sm:gap-3 bg-[#2693d0] text-white px-6 sm:px-10 py-4 sm:py-5 rounded-lg font-semibold text-base sm:text-lg hover:bg-[#1e7ab8] focus:outline-none focus:ring-2 focus:ring-[#2693d0] focus:ring-offset-2 transition-all duration-300"
                aria-label="Book a confidential conversation with RESILIGENT"
              >
                Book a Confidential Conversation
                <ArrowRight size={20} aria-hidden="true" />
              </a>
              <button
                onClick={() => setShowDownloadModal(true)}
                className="inline-flex items-center gap-2 sm:gap-3 bg-white text-[#2693d0] border-2 border-[#2693d0] px-6 sm:px-10 py-4 sm:py-5 rounded-lg font-semibold text-base sm:text-lg hover:bg-[#2693d0]/5 focus:outline-none focus:ring-2 focus:ring-[#2693d0] focus:ring-offset-2 transition-all duration-300"
                aria-label="Download RESILIGENT Corporate Profile"
              >
                Download Corporate Profile
                <Download size={20} aria-hidden="true" />
              </button>
            </div>
          </div>
        </section>

        {/* Download Modal */}
        {showDownloadModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowDownloadModal(false)}>
            <div 
              className="bg-[#ffffff] rounded-2xl p-6 sm:p-8 max-w-md w-full shadow-2xl border border-slate-200"
              style={{ backgroundColor: '#ffffff' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-slate-900 font-bold text-xl sm:text-2xl">Download Corporate Profile</h3>
                <button 
                  onClick={() => setShowDownloadModal(false)}
                  className="text-slate-400 hover:text-slate-600 transition-colors"
                  aria-label="Close modal"
                >
                  <X size={24} />
                </button>
              </div>
              <p className="text-slate-600 text-sm sm:text-base mb-6">
                Please provide your details to download our corporate profile.
              </p>
              <form onSubmit={handleDownloadSubmit} className="space-y-4">
                <div>
                  <label htmlFor="download-name" className="block text-slate-700 text-sm font-medium mb-1">
                    Full Name *
                  </label>
                  <input
                    id="download-name"
                    type="text"
                    required
                    value={downloadForm.name}
                    onChange={(e) => setDownloadForm({...downloadForm, name: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#2693d0] focus:border-transparent outline-none transition-all"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label htmlFor="download-company" className="block text-slate-700 text-sm font-medium mb-1">
                    Company Name *
                  </label>
                  <input
                    id="download-company"
                    type="text"
                    required
                    value={downloadForm.company}
                    onChange={(e) => setDownloadForm({...downloadForm, company: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#2693d0] focus:border-transparent outline-none transition-all"
                    placeholder="Your company"
                  />
                </div>
                <div>
                  <label htmlFor="download-email" className="block text-slate-700 text-sm font-medium mb-1">
                    Email Address *
                  </label>
                  <input
                    id="download-email"
                    type="email"
                    required
                    value={downloadForm.email}
                    onChange={(e) => setDownloadForm({...downloadForm, email: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#2693d0] focus:border-transparent outline-none transition-all"
                    placeholder="your.email@company.com"
                  />
                </div>
                {downloadError && (
                  <p className="text-red-500 text-sm">{downloadError}</p>
                )}
                <button
                  type="submit"
                  disabled={downloadLoading}
                  className="w-full bg-[#2693d0] text-white py-3 rounded-lg font-semibold hover:bg-[#1e7ab8] focus:outline-none focus:ring-2 focus:ring-[#2693d0] focus:ring-offset-2 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {downloadLoading ? 'Processing...' : (
                    <>
                      Download Profile
                      <Download size={18} />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        )}
      </main>
      
      <Footer hidePreFooterCta={true} />
    </div>
  );
};

export default HomePage;
